import { abbreviations } from './abbreviations.js';

export const formatDuration = (value, options = {
  showZeroParts: false,
  pad: false,
  precision: 'seconds',
  language: 'en'
}) => {

  const parsedValue = parseFloat(value, 10);

  if (isNaN(parsedValue)) {
    return value ? `${value}` : '';
  }

  const formatOptions = {
    fixedWidth: optionValue(options.pad, { defaultValue: false, options: [true, false] }),
    showZeroParts: optionValue(options.showZeroParts, { defaultValue: false, options: [true, false] }),
    precision: optionValue(options.precision, {
      defaultValue: 'seconds',
      options: ['days', 'hours', 'minutes', 'seconds', 'milliseconds', 'auto']
    }),
    language: optionValue(options.language, { defaultValue: 'en', options: Object.keys(abbreviations) })
  };

  const partValues = secondsToDurationParts(Math.abs(parsedValue));

  const negativePart = parsedValue < 0 ? '-' : '';

  return [negativePart, ...displayParts(partValues, formatOptions)].filter(v => v).join(' ');
};

const optionValue = (value, { defaultValue, options }) => {
  const isValid = value === undefined || options === undefined || options.includes(value);
  return value !== undefined && isValid ? value : defaultValue;
};

const secondsToDurationParts = seconds => {
  const lengthInSeconds = { days: 86400, hours: 3600, minutes: 60 };
  return {
    days: Math.floor(seconds / lengthInSeconds.days),
    hours: Math.floor((seconds % lengthInSeconds.days) / lengthInSeconds.hours),
    minutes: Math.floor((seconds % lengthInSeconds.hours) / lengthInSeconds.minutes),
    seconds: Math.floor(seconds % lengthInSeconds.minutes),
    milliseconds: (seconds % 1).toFixed(3) * 1000
  };
};

const displayParts = (partValues, { showZeroParts, fixedWidth, precision, language }) => {
  const precisionToApply = precision === 'auto' ? autoPrecision(partValues) : precision;
  const { days, hours, minutes, seconds, milliseconds } = dropValuesBelowPrecision(partValues, precisionToApply);

  const shouldShow = shouldShowParts({ days, hours, minutes, seconds, milliseconds }, showZeroParts, precisionToApply);

  const minWidth = fixedWidth ? 2 : 1;

  const units = {
    'd': translateUnit('d', language),
    'h': translateUnit('h', language),
    'm': translateUnit('m', language),
    's': translateUnit('s', language),
    'ms': translateUnit('ms', language)
  };

  return [
    shouldShow.days ? `${pad(days, minWidth)}${units.d}` : '',
    shouldShow.hours ? `${pad(hours, minWidth)}${units.h}` : '',
    shouldShow.minutes ? `${pad(minutes, minWidth)}${units.m}` : '',
    shouldShow.seconds ? `${pad(seconds, minWidth)}${units.s}` : '',
    shouldShow.milliseconds ? `${pad(milliseconds, 3)}${units.ms}` : ''
  ];
};

const dropValuesBelowPrecision = ({ days, hours, minutes, seconds, milliseconds }, precision) => {
  return {
    days,
    hours: !['days'].includes(precision) ? hours : undefined,
    minutes: !['days', 'hours'].includes(precision) ? minutes : undefined,
    seconds: !['days', 'hours', 'minutes'].includes(precision) ? seconds : undefined,
    milliseconds: !['days', 'hours', 'minutes', 'seconds'].includes(precision) ? milliseconds : undefined
  };
};

const autoPrecision = (partValues) => {
  if (partValues.days) { return 'hours'; }
  if (partValues.hours) { return 'minutes'; }
  if (partValues.minutes || partValues.seconds) { return 'seconds'; }
  if (partValues.milliseconds) { return 'milliseconds'; }

  return 'seconds';
};

const shouldShowParts = ({ days, hours, minutes, seconds, milliseconds }, showZeroParts, appliedPrecision) => {
  const shouldShowWithPrecision = (shouldShowWithoutPrecision, value) => {
    return value !== undefined && shouldShowWithoutPrecision;
  };

  const shouldShow = {
    days: !!days,
    hours: shouldShowWithPrecision(hours || (days && showZeroParts), hours),
    minutes: shouldShowWithPrecision(minutes || ((hours || days) && showZeroParts), minutes),
    seconds: shouldShowWithPrecision(seconds || ((hours || days || minutes) && showZeroParts), seconds),
    milliseconds: shouldShowWithPrecision(milliseconds || showZeroParts, milliseconds)
  };

  if (Object.values(shouldShow).every(v => !v)) {
    shouldShow[appliedPrecision] = true;
  }

  return shouldShow;
};

const pad = (value, minWidth) => `${value}`.padStart(minWidth, '0');

const translateUnit = (unit, language) => {
  const unitDictionary = {
    'd': 'days',
    'h': 'hours',
    'm': 'minutes',
    's': 'seconds',
    'ms': 'milliseconds'
  };
  const unitName = unitDictionary[unit];
  return abbreviations[language][unitName];
};
